import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type WheelEvent } from "react";
import s from "./Map.module.css";
import {
  boundsOf,
  isFinite as boundsFinite,
  pathFor,
  pointsOf,
  styleFor,
  type LonLat,
  type MapFeature,
  type MapLayer,
  type Projection,
} from "../../domain/geo";
import { MARKER_COLOUR, type Marker } from "./markers";
import { RealBase, type BaseMode } from "./RealBase";
import { t } from "../../i18n";

/** A team on the map: where the engine says it is, and what it is doing. */
export interface TeamMarker {
  k: string;
  name: string;
  at: LonLat;
  colour: string;
  label: string;
  moving: boolean;
}

export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  layers: MapLayer[];
  features: MapFeature[];
  markers: Marker[];
  projection: Projection;
  selected?: string;
  onSelect(id: string | undefined): void;
  /** Layer keys whose features respond to hover/click (assets, signs …). */
  interactiveLayers?: readonly string[];
  /** The survey scan's linework, or the airfield painted as it looks by day or by night. */
  base?: BaseMode;
  /** A click on nothing in particular — a place to send a team. */
  onMapClick?(p: LonLat): void;
  teams?: TeamMarker[];
}

/**
 * The drawing. Everything is in metres about the site centre; the viewBox is
 * the camera. Wheel zooms about the cursor, drag pans, double-click resets.
 * Marker radius scales with the view so a lamp is visible at 5 km and does
 * not swallow the runway at 200 m — the console's lesson, kept.
 */
export function AirfieldSvg({
  layers,
  features,
  markers,
  projection: P,
  selected,
  onSelect,
  interactiveLayers = [],
  base = "drawing",
  onMapClick,
  teams = [],
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [view, setView] = useState<ViewBox | null>(null);
  const [hover, setHoverState] = useState<{ text: string; sub?: string; x: number; y: number } | null>(null);
  /* Tooltip coordinates are stored relative to the stage, converted in the handler rather than during render. */
  const setHover = (
    v:
      | ((
          h: { text: string; sub?: string; x: number; y: number } | null,
        ) => { text: string; sub?: string; x: number; y: number } | null)
      | { text: string; sub?: string; x: number; y: number }
      | null,
  ) => {
    const r = svgRef.current?.getBoundingClientRect();
    const rel = (h: { text: string; sub?: string; x: number; y: number } | null) =>
      h && r ? { ...h, x: h.x - r.left, y: h.y - r.top } : h;
    if (typeof v === "function")
      setHoverState((prev) => {
        /* prev is already relative; the updater supplies client coords for x/y */
        const next = v(prev ? { ...prev, x: prev.x + (r?.left ?? 0), y: prev.y + (r?.top ?? 0) } : prev);
        return rel(next);
      });
    else setHoverState(rel(v));
  };
  const drag = useRef<{ x: number; y: number; view: ViewBox; moved: boolean } | null>(null);

  const bounds = useMemo(() => boundsOf(features, P), [features, P]);
  const fit = useCallback((): ViewBox | null => {
    if (!boundsFinite(bounds)) return null;
    const w = bounds.maxX - bounds.minX;
    const h = bounds.maxY - bounds.minY;
    const pad = Math.max(w, h) * 0.05 + 20;
    return { x: bounds.minX - pad, y: bounds.minY - pad, w: w + pad * 2, h: h + pad * 2 };
  }, [bounds]);

  /* The stage's pixel size, observed rather than read from the ref during render. */
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 1200, h: 675 });
  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Until the user moves the camera, the view is the fit; a layer toggle then re-fits, a pan does not. */
  const camera = view ?? fit();

  const layerByKey = useMemo(() => new Map(layers.map((l) => [l.key, l])), [layers]);
  const ordered = useMemo(() => {
    const z = (k: string) => layerByKey.get(k)?.zIndex ?? 0;
    return [...features].sort((a, b) => z(a.layer) - z(b.layer));
  }, [features, layerByKey]);

  const scale = camera ? camera.w / 1000 : 1; // viewBox units per "px-ish" unit
  const dot = Math.max(scale * 2.2, 0.6);
  const markerR = Math.max(scale * 7, 3);

  const clientToView = (e: { clientX: number; clientY: number }, v: ViewBox): [number, number] => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const r = svg.getBoundingClientRect();
    return [v.x + ((e.clientX - r.left) / r.width) * v.w, v.y + ((e.clientY - r.top) / r.height) * v.h];
  };

  const onWheel = (e: WheelEvent<SVGSVGElement>) => {
    if (!camera) return;
    const v = fitAspect(camera, size.w / size.h);
    e.preventDefault();
    const [mx, my] = clientToView(e, v);
    const f = e.deltaY > 0 ? 1.18 : 1 / 1.18;
    const w = Math.min(Math.max(v.w * f, 40), 60_000);
    const h = (w / v.w) * v.h;
    setView({ x: mx - ((mx - v.x) * w) / v.w, y: my - ((my - v.y) * h) / v.h, w, h });
  };
  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (!camera) return;
    drag.current = { x: e.clientX, y: e.clientY, view: fitAspect(camera, size.w / size.h), moved: false };
    (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    const svg = svgRef.current;
    if (!d || !svg) return;
    const r = svg.getBoundingClientRect();
    const dx = ((e.clientX - d.x) / r.width) * d.view.w;
    const dy = ((e.clientY - d.y) / r.height) * d.view.h;
    if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 4) d.moved = true;
    setView({ ...d.view, x: d.view.x - dx, y: d.view.y - dy });
  };
  /* a drag that moved is a pan, not a click; remembered past pointer-up for the click that follows */
  const panned = useRef(false);
  const onPointerUp = () => {
    panned.current = !!drag.current?.moved;
    drag.current = null;
  };

  if (!camera) {
    return (
      <div className={s.stage}>
        <div className={s.empty}>
          No drawing for this site yet. Ingest a layout, or an asset register with positions, and the map appears.
        </div>
      </div>
    );
  }

  const vb = fitAspect(camera, size.w / size.h);
  const metresPerPx = vb.w / size.w;
  const scaleM = niceScale(metresPerPx * 120);

  const real = base !== "drawing";
  return (
    <div
      className={s.stage}
      dir="ltr"
      style={real ? { background: base === "night" ? "#07090B" : "#C9B48C" } : undefined}
    >
      {real && <RealBase vb={vb} size={size} mode={base} projection={P} />}
      <svg
        ref={svgRef}
        className={s.svg}
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => {
          onPointerUp();
          setHover(null);
        }}
        onDoubleClick={() => setView(null)}
        onClick={(e) => {
          if (e.target !== e.currentTarget || panned.current) return;
          onSelect(undefined);
          if (onMapClick) {
            const [x, y] = clientToView(e, vb);
            onMapClick(P.toLonLat(x, y));
          }
        }}
        role="img"
        aria-label={t("Dynamic map")}
      >
        {ordered.map((f) => {
          const st = styleFor(layerByKey.get(f.layer), f.layer);
          const interactive = interactiveLayers.includes(f.layer);
          const label = String(f.props.name ?? f.props.tag ?? f.layer);
          const hoverProps = interactive
            ? {
                onPointerEnter: (e: PointerEvent) =>
                  setHover({
                    text: label,
                    sub: `${f.layer}${f.props.tag ? ` · ${String(f.props.tag)}` : ""}`,
                    x: e.clientX,
                    y: e.clientY,
                  }),
                onPointerMove: (e: PointerEvent) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h)),
                onPointerLeave: () => setHover(null),
                style: { pointerEvents: "all" as const, cursor: "pointer" },
              }
            : { style: { pointerEvents: "none" as const } };
          const pts = pointsOf(f.geometry);
          if (real && !pts.length && !interactive) return null;
          if (pts.length) {
            return (
              <g key={f.id} {...hoverProps}>
                {pts.map((p, i) => {
                  const [x, y] = P.toXY(p);
                  const unserviceable = f.props.status === "unserviceable";
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r={dot * (st.widthPx ?? 1)}
                      fill={unserviceable ? "#e0524e" : (st.fill ?? st.stroke ?? "#8ca3b3")}
                      opacity={real ? 0.35 : (st.opacity ?? 1)}
                    />
                  );
                })}
              </g>
            );
          }
          const d = pathFor(f.geometry, P);
          if (!d) return null;
          const isArea = f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon";
          const markingColour = f.layer === "markings" ? markingStroke(f.props.colour) : undefined;
          return (
            <path
              key={f.id}
              d={d}
              fill={isArea && !real ? (st.fill ?? "none") : "none"}
              stroke={markingColour ?? st.stroke ?? "none"}
              strokeWidth={st.widthPx ?? 1}
              strokeDasharray={f.layer === "perimeter" ? "6 4" : undefined}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={real ? 0.45 : (st.opacity ?? 1)}
              vectorEffect="non-scaling-stroke"
              {...hoverProps}
            />
          );
        })}

        {markers.map((m) => {
          const [x, y] = P.toXY(m.at);
          const sel = m.id === selected;
          const r = markerR * (m.weight <= 1 ? 1.35 : m.weight === 2 ? 1.15 : 1);
          return (
            <g
              key={m.id}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(m.id);
              }}
              onPointerEnter={(e) => setHover({ text: m.title, sub: m.subtitle, x: e.clientX, y: e.clientY })}
              onPointerMove={(e) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))}
              onPointerLeave={() => setHover(null)}
              data-testid={`marker-${m.kind}`}
            >
              {sel && (
                <circle
                  cx={x}
                  cy={y}
                  r={r * 2.2}
                  fill="none"
                  stroke={MARKER_COLOUR[m.kind]}
                  strokeWidth={scale * 1.2}
                  opacity={0.7}
                />
              )}
              {m.kind === "alert" ? (
                <polygon
                  points={`${x},${y - r * 1.15} ${x + r * 1.05},${y + r * 0.75} ${x - r * 1.05},${y + r * 0.75}`}
                  fill={MARKER_COLOUR[m.kind]}
                  stroke="#070c11"
                  strokeWidth={scale * 0.8}
                />
              ) : m.kind === "finding" ? (
                <rect
                  x={x - r * 0.85}
                  y={y - r * 0.85}
                  width={r * 1.7}
                  height={r * 1.7}
                  transform={`rotate(45 ${x} ${y})`}
                  fill={MARKER_COLOUR[m.kind]}
                  stroke="#070c11"
                  strokeWidth={scale * 0.8}
                />
              ) : (
                <circle cx={x} cy={y} r={r} fill={MARKER_COLOUR[m.kind]} stroke="#070c11" strokeWidth={scale * 0.8} />
              )}
            </g>
          );
        })}
        {teams.map((t) => {
          const [x, y] = P.toXY(t.at);
          const r = markerR * 1.1;
          return (
            <g
              key={t.k}
              data-testid="team-marker"
              onPointerEnter={(e) => setHover({ text: t.name, sub: t.label, x: e.clientX, y: e.clientY })}
              onPointerMove={(e) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))}
              onPointerLeave={() => setHover(null)}
              style={{ pointerEvents: "all" }}
            >
              {t.moving && (
                <circle
                  cx={x}
                  cy={y}
                  r={r * 2.4}
                  fill="none"
                  stroke={t.colour}
                  strokeWidth={scale * 1.2}
                  opacity={0.55}
                />
              )}
              <rect
                x={x - r}
                y={y - r * 0.6}
                width={r * 2}
                height={r * 1.2}
                rx={r * 0.25}
                fill={t.colour}
                stroke="#070c11"
                strokeWidth={scale * 0.8}
              />
              <text
                x={x}
                y={y - r * 1.6}
                textAnchor="middle"
                fontSize={Math.max(scale * 8, 9)}
                fontFamily="ui-monospace, Menlo, monospace"
                fontWeight={700}
                fill={t.colour}
                stroke="#070c11"
                strokeWidth={scale * 0.5}
                paintOrder="stroke"
              >
                {t.name.toUpperCase()}
              </text>
            </g>
          );
        })}
      </svg>

      <div className={s.hud}>
        <button type="button" aria-label={t("Zoom in")} onClick={() => setView(zoomBy(vb, 1 / 1.4))}>
          +
        </button>
        <button type="button" aria-label={t("Zoom out")} onClick={() => setView(zoomBy(vb, 1.4))}>
          −
        </button>
        <button
          type="button"
          aria-label={t("Fit to drawing")}
          title={t("Fit (or double-click the map)")}
          onClick={() => setView(fit())}
        >
          ⊡
        </button>
        <span className={s.scale}>
          <span className={s.scaleBar} style={{ width: `${scaleM / metresPerPx}px` }} />
          {scaleM >= 1000 ? `${scaleM / 1000} km` : `${scaleM} m`}
        </span>
      </div>

      {hover && (
        <div className={s.tip} style={{ left: hover.x, top: hover.y }}>
          {hover.text}
          {hover.sub && <div className={s.tipSub}>{hover.sub}</div>}
        </div>
      )}
    </div>
  );
}

function fitAspect(v: ViewBox, aspect: number): ViewBox {
  const current = v.w / v.h;
  if (Math.abs(current - aspect) < 1e-3) return v;
  if (current < aspect) {
    const w = v.h * aspect;
    return { x: v.x - (w - v.w) / 2, y: v.y, w, h: v.h };
  }
  const h = v.w / aspect;
  return { x: v.x, y: v.y - (h - v.h) / 2, w: v.w, h };
}

function zoomBy(v: ViewBox, f: number): ViewBox {
  const w = Math.min(Math.max(v.w * f, 40), 60_000);
  const h = v.h * (w / v.w);
  return { x: v.x + (v.w - w) / 2, y: v.y + (v.h - h) / 2, w, h };
}

function niceScale(m: number): number {
  const steps = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
  return steps.find((x) => x >= m) ?? 5000;
}

function markingStroke(colour: unknown): string | undefined {
  if (colour === "yellow") return "#FCD34D";
  if (colour === "white") return "#F9FAFB";
  if (colour === "red") return "#EF4444";
  return undefined;
}
